import { cn } from '../cn.js';

type Size = 'sm' | 'md' | 'lg' | 'xl';

export interface ClockDigitsProps {
  /** Already formatted — `02:14:09`, `3d 04:10`, or a wall clock like `05:36:12 PM`. */
  value: string;
  /** Omit to inherit the surrounding font size (an inline timer inside a sentence or a pill). */
  size?: Size;
  /** A ticking clock: separators breathe, and each group eases in as it changes. */
  live?: boolean;
  /**
   * Renders the last `:`-group smaller and quieter — hours and minutes are what a person reads at
   * a glance, the seconds are only there to show the clock is alive.
   */
  minorSeconds?: boolean;
  className?: string;
}

const SIZES: Record<Size, string> = {
  sm: 'text-sm font-semibold',
  md: 'text-lg font-bold',
  lg: 'text-2xl font-bold',
  xl: 'text-4xl font-extrabold sm:text-5xl',
};

/**
 * The digits of a clock or timer, typeset rather than printed: display face, tabular figures so
 * nothing shifts width as it ticks, softened separators, and (when `live`) a gentle ease-in on
 * whichever group just changed. `ElapsedTimer`, `CountdownTimer`, the working-time ring and the
 * attendance wall clock all render through this, so every live clock in the app reads as one
 * family.
 *
 * Purely presentational — it never ticks by itself. The caller owns the interval and passes a
 * fresh `value`; keying each group on its own text is what makes only the changed group animate.
 * The text content is still the plain `value` (separators are real `:` characters), so a
 * screen reader or a test reads `02:14:09`, not a pile of spans.
 */
export function ClockDigits({
  value,
  size,
  live = false,
  minorSeconds = false,
  className,
}: ClockDigitsProps) {
  const groups = value.split(':');
  // A trailing meridiem or unit ("12 PM") belongs after the last group, set as a quiet label.
  const lastRaw = groups[groups.length - 1] ?? '';
  const suffixMatch = /^(\S+)\s+(.+)$/.exec(lastRaw);
  if (suffixMatch) groups[groups.length - 1] = suffixMatch[1]!;
  const suffix = suffixMatch?.[2];

  return (
    <span
      className={cn(
        'inline-flex items-baseline whitespace-nowrap font-display leading-none tracking-tight tabular-nums',
        size && SIZES[size],
        className,
      )}
    >
      {groups.map((group, index) => {
        const isLast = index === groups.length - 1;
        const isMinor = minorSeconds && isLast && groups.length > 2;
        return (
          <span key={index} className="inline-flex items-baseline">
            {index > 0 && (
              <span
                className={cn(
                  'mx-[0.04em] -translate-y-[0.06em] font-semibold',
                  isMinor && 'text-[0.62em]',
                  live ? 'opacity-55 motion-safe:animate-clock-breathe' : 'opacity-45',
                )}
              >
                :
              </span>
            )}
            <span
              // Keyed on its own text: a new value remounts this group alone, which replays the
              // entrance — the seconds glide in every tick while hours and minutes sit still.
              key={live ? group : undefined}
              className={cn(
                'inline-block',
                isMinor && 'text-[0.62em] opacity-60',
                live && 'duration-300 ease-out animate-in fade-in-40 slide-in-from-bottom-[0.18em]',
              )}
            >
              {group}
            </span>
          </span>
        );
      })}
      {suffix && (
        <span className="ml-[0.3em] text-[0.36em] font-semibold uppercase tracking-wider opacity-55">
          {suffix}
        </span>
      )}
    </span>
  );
}
